import { useMemo } from 'react';
import { Boxes } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CellBar } from '@/components/molecules/org-admin/BudgetMeter';
import {
  OrgAdminEmptyState,
  OrgAdminTableSkeleton,
} from '@/components/molecules/org-admin/OrgAdminStates';
import { SortableHeader, sortRows, useSort } from '@/components/molecules/org-admin/OrgAdminTable';
import { Section } from '@/components/molecules/org-admin/OrgAdminSection';
import type { ServiceMetricRow } from '@/services/org-admin/analysis';
import { formatCurrencyUsd, formatTokenCount } from '../usageFormat';

/**
 * "Where is the money going, and what is breaking?" — one table, replacing two tables and a chart.
 *
 * WHY THIS IS A TABLE NOW. It was a ranked bar chart with a three-way measure toggle
 * (Cost / Runs / Failures). A measure toggle is a HIDING mechanism: it shows a third of the answer
 * and asks the reader to remember the other two. But the comparison that actually drives a decision
 * is across measures *within one feature* — "Weave is our biggest cost AND it fails 12% of the
 * time" — and a bar chart ranked on one axis is structurally incapable of showing that, however many
 * toggles you add. A table shows every measure per row at once, and an in-cell bar keeps the
 * magnitude comparison the chart was there for.
 *
 * It also replaces the Usage tab's own near-identical per-service table, which read the same events
 * from the same endpoint under a different title. `ServiceMetricRow` is the richer model of the two —
 * it already carries share of spend, failure rate and average latency — so nothing was lost by
 * keeping this one.
 *
 * THE NAMING RULE, restated because it is easy to undo. There is no "Succeeded" column and there
 * must not be one. The service report carries `run_count` and `failed_count` and nothing else, and
 * `run_count` spans runs that were cancelled or are still in flight. `Runs` and `Failed` are both
 * real fields and together carry the whole answer, which sidesteps the naming problem instead of
 * solving it with a word the contract cannot support.
 *
 * Latency stays as a column and is deliberately not offered as a sort-by-default or a chart: it is a
 * performance measure, and on a page about controlling spend it cannot lead to a decision. Keeping
 * it costs nothing; leading with it would mislead.
 */

type FeatureSortKey = 'feature' | 'spend' | 'runs' | 'failed' | 'failureRate' | 'latency';

export interface ByFeatureSectionProps {
  rows: ServiceMetricRow[];
  isLoading: boolean;
  /** Names the reporting range in the caption, so an exported screenshot carries its own scope. */
  rangeLabel: string;
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(value < 0.1 && value > 0 ? 1 : 0)}%`;
}

function formatMillis(value: number): string {
  return value >= 1000 ? `${(value / 1000).toFixed(1)}s` : `${Math.round(value)}ms`;
}

export function ByFeatureSection({ rows, isLoading, rangeLabel }: ByFeatureSectionProps) {
  // Spend descending is the useful default: an admin opens this to find what is costing them money,
  // not to read an alphabetical feature list.
  const { sort, toggle } = useSort<FeatureSortKey>({ key: 'spend', direction: 'desc' });

  // Scaled to the biggest feature in view — the question is "how big relative to what else", and a
  // fixed ceiling would flatten every row in an org with one dominant feature.
  const maxSpend = useMemo(() => Math.max(0, ...rows.map((row) => row.costUsd)), [rows]);

  const sorted = useMemo(
    () =>
      sortRows(rows, sort, (row, key) => {
        switch (key) {
          case 'feature':
            return row.label;
          case 'spend':
            return row.costUsd;
          case 'runs':
            return row.runs;
          case 'failed':
            return row.failed;
          case 'failureRate':
            /*
             * `null` means "no runs", not "0% failure". Sorted as -1 so those rows sink below a
             * genuine 0%, rather than tying with the features that ran cleanly — a feature nobody
             * used is not the most reliable one.
             */
            return row.failureRate ?? -1;
          case 'latency':
            return row.avgLatencyMs ?? -1;
        }
      }),
    [rows, sort],
  );

  return (
    <Section title="What is it going on?">
      <section className="oa-panel flex flex-col" aria-labelledby="oa-byfeature-title">
        <header className="oa-toolbar py-2.5">
          <h3 id="oa-byfeature-title" className="oa-title">
            By feature
          </h3>
          <p className="oa-meta mt-0.5">
            What each feature cost, how much it was used, and how much of that failed.
          </p>
        </header>

        {/* The table's own horizontal scroll. Six columns genuinely do not fit a phone-width panel,
            and the document must never be what scrolls sideways. */}
        <div className="overflow-x-auto">
          {isLoading ? (
            <OrgAdminTableSkeleton
              rows={5}
              columns={['26%', '110px', '80px', '80px', '90px', '90px']}
              label="Loading feature usage…"
            />
          ) : sorted.length === 0 ? (
            <OrgAdminEmptyState
              icon={Boxes}
              title="No feature activity"
              description={`No feature reported a run ${rangeLabel}.`}
            />
          ) : (
            <table className="oa-table">
              <caption className="sr-only">
                Spend, runs, failures and average run time per feature over the {rangeLabel}
              </caption>
              <thead>
                <tr>
                  <SortableHeader columnKey="feature" label="Feature" sort={sort} onSort={toggle} />
                  <SortableHeader
                    columnKey="spend"
                    label="Spend"
                    sort={sort}
                    onSort={toggle}
                    align="right"
                    width="130px"
                  />
                  <SortableHeader
                    columnKey="runs"
                    label="Runs"
                    sort={sort}
                    onSort={toggle}
                    align="right"
                    width="90px"
                  />
                  <SortableHeader
                    columnKey="failed"
                    label="Failed"
                    sort={sort}
                    onSort={toggle}
                    align="right"
                    width="90px"
                  />
                  <SortableHeader
                    columnKey="failureRate"
                    label="Failure rate"
                    sort={sort}
                    onSort={toggle}
                    align="right"
                    width="110px"
                  />
                  <SortableHeader
                    columnKey="latency"
                    label="Avg run"
                    sort={sort}
                    onSort={toggle}
                    align="right"
                    width="100px"
                  />
                </tr>
              </thead>
              <tbody>
                {sorted.map((row) => (
                  <tr key={row.service}>
                    <td className="truncate font-medium" title={row.label}>
                      {row.label}
                      {/* Share rides under the name rather than taking a column of its own: it is
                          context for the spend figure, not a measure anyone sorts by. */}
                      <span className="oa-meta ml-1.5">
                        {formatPercent(row.shareOfTotal)} of spend
                      </span>
                    </td>
                    {/* Spend is what the table is sorted and read on, so it is the column that gets
                        the comparison bar — the rest stay plain rather than turning every cell into
                        a chart. */}
                    <td className="oa-num">
                      <CellBar value={row.costUsd} max={maxSpend}>
                        {formatCurrencyUsd(row.costUsd)}
                      </CellBar>
                    </td>
                    <td className="oa-num">{row.runs.toLocaleString()}</td>
                    <td className={cn('oa-num', row.failed > 0 && 'text-warning-strong')}>
                      {row.failed.toLocaleString()}
                    </td>
                    {/* An em dash, never 0%: a feature with no runs has no failure rate, and
                        rendering 0% would claim it is perfectly reliable. */}
                    <td
                      className={cn(
                        'oa-num',
                        row.failureRate !== null && row.failureRate > 0 && 'text-warning-strong',
                      )}
                    >
                      {row.failureRate === null ? '—' : formatPercent(row.failureRate)}
                    </td>
                    <td className="oa-num">
                      {row.avgLatencyMs === null ? '—' : formatMillis(row.avgLatencyMs)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Token counts sit in the footer, not in columns. Nobody opens an admin panel to learn a
            token count — they open it to learn a dollar figure and then ask what drove it — but the
            totals are cheap to state and answer "why is this feature expensive?". */}
        {!isLoading && sorted.length > 0 && (
          <div className="oa-footer-rail flex flex-wrap items-center gap-x-4 gap-y-1 py-1.5">
            <p className="oa-meta">
              {formatTokenCount(sorted.reduce((sum, row) => sum + row.inputTokens, 0))} tokens in ·{' '}
              {formatTokenCount(sorted.reduce((sum, row) => sum + row.outputTokens, 0))} tokens out
            </p>
          </div>
        )}
      </section>
    </Section>
  );
}
